import { BotMessageSquare, Building } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Organization } from '@/types'
import { Skeleton } from '@/components/ui/skeleton'

const examplePrompts = [
  'Analyze the recent incident report for the checkout service',
  "Check the health of the 'user-database' service",
  'Troubleshoot high latency on the API gateway',
  'Generate a summary of system alerts from the last 24 hours',
]

interface EmptyChatProps {
  onPromptClick: (prompt: string) => void
  organizations: Organization[]
  selectedOrgId: string | undefined
  setSelectedOrgId: (id: string) => void
  isLoadingOrgs: boolean
}

export const EmptyChat = ({
  onPromptClick,
  organizations,
  selectedOrgId,
  setSelectedOrgId,
  isLoadingOrgs,
}: EmptyChatProps) => {
  return (
    <div className="flex flex-1 items-center justify-center">
      <div className="container max-w-3xl mx-auto p-4 text-center">
        <BotMessageSquare className="mx-auto h-12 w-12 text-muted-foreground" />
        <h2 className="mt-4 text-2xl font-semibold">
          How can I help you today?
        </h2>
        <p className="mt-2 text-muted-foreground">
          Select an organization and start a conversation.
        </p>

        <div className="mt-6 max-w-sm mx-auto">
          {isLoadingOrgs ? (
            <Skeleton className="h-10 w-full" />
          ) : (
            <Select
              value={selectedOrgId}
              onValueChange={setSelectedOrgId}
              disabled={organizations.length === 0}
            >
              <SelectTrigger>
                <div className="flex items-center gap-2">
                  <Building className="h-4 w-4 text-muted-foreground" />
                  <SelectValue placeholder="Select an organization..." />
                </div>
              </SelectTrigger>
              <SelectContent>
                {organizations.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name} {org.isDefault && '(Default)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {examplePrompts.map((prompt) => (
            <Card
              key={prompt}
              onClick={() => onPromptClick(prompt)}
              className="cursor-pointer text-left p-4 transition-all hover:bg-accent"
            >
              <CardContent className="p-0">
                <p className="text-sm font-medium">{prompt}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  )
}
